// Moteur créneaux/capacité (LOT 7 §3) — module 100 % PUR, zéro état, zéro
// dépendance (types seuls). Remplace l'usage UI de `heure-proposee` (fichier et
// tests v1 conservés, plus référencés par l'UI). L'heure produite est TOUJOURS
// une ESTIMATION, jamais une garantie (§3.3).
//
// Modèle (§3.1-3.2) :
// - Grille de créneaux alignée sur les multiples horloge de `dureeCreneauMin` ;
//   départ t0 = plus petit multiple ≥ now (le créneau entamé n'est jamais
//   utilisé → marge naturelle 0-5 min, remplace `margeMin`).
// - Charge engagée = Σ qty des commandes NON terminales (commandee …
//   en_finalisation ; prete/cloturee/annulee exclues), placées par échéance la
//   plus proche d'abord (heure promise croissante, puis createdAt) dans les
//   créneaux depuis t0. Estimation volontairement CONSERVATRICE : les commandes
//   en cours sont recomptées entières (pas de statut par pizza en voie 1) — le
//   moteur peut surestimer la charge ; c'est assumé.
// - Heure absente/invalide (cas normalement empêché par l'UI, §3.5) : le moteur
//   substitue la première dispo courante — la commande est triée sur cette
//   échéance, `evalPromesse` retourne 'serre', AUCUNE exception levée.
import type { StatutCommande } from '../db/schema';

export interface HeureCapaciteConfig {
  /** Pizzas par créneau (hypothèse de démarrage : 2 — configurable). */
  capaciteParCreneau: number;
  /** Durée d'un créneau en minutes (hypothèse de démarrage : 5 — configurable). */
  dureeCreneauMin: number;
}

// Vue minimale d'une commande pour le moteur (découplée de Dexie — pur).
export interface ChargeCommande {
  id: number;
  /** Σ qty des lignes de la commande. */
  pizzas: number;
  /** Heure promise (epoch ms) — peut être absente/invalide (§3.5). */
  promise: number | null | undefined;
  createdAt: number;
  statut: StatutCommande;
}

export type EvalPromesse = 'ok' | 'serre' | 'intenable';

// Statut de délai par commande — cascade binding (arbitrage Hamid 11/07/2026,
// amende §2/§3.6 : partition 4 états, premier vrai gagne) :
//   0. prete/cloturee/annulee -> aucun message.
//   1. now > h                -> retard  (X = ceil min, minimum 1 — jamais 0).
//   2. fin > h                -> capacite (promesse future, intenable par la charge).
//   3. fin > h - 1 créneau    -> risque   (aucune marge).
//   4. sinon                  -> a_l_heure.
export type StatutDelai =
  | { type: 'retard'; retardMin: number }
  | { type: 'capacite' }
  | { type: 'risque' }
  | { type: 'a_l_heure' };

export interface ChargeActuelle {
  pizzas: number;
  creneaux: number;
  premiereDispo: number;
}

const MS_MIN = 60_000;

// Statuts comptés dans la charge engagée (§3.2). `prete` et terminales exclues.
const STATUTS_CHARGE: readonly StatutCommande[] = [
  'commandee',
  'priorisee',
  'en_preparation',
  'enfournee',
  'en_finalisation',
];

function creneauMs(config: HeureCapaciteConfig): number {
  return config.dureeCreneauMin * MS_MIN;
}

// t0 = plus petit multiple horloge de `dureeCreneauMin` ≥ now (§3.1).
export function premierCreneau(
  now: number,
  config: HeureCapaciteConfig,
): number {
  const c = creneauMs(config);
  return Math.ceil(now / c) * c;
}

function heureValide(h: number | null | undefined): h is number {
  return typeof h === 'number' && Number.isFinite(h);
}

function enCharge(commandes: readonly ChargeCommande[]): ChargeCommande[] {
  return commandes.filter(
    (c) => STATUTS_CHARGE.includes(c.statut) && c.pizzas > 0,
  );
}

// Charge engagée courante (§3.2) : Y pizzas, ~N créneaux, fin de charge estimée.
// `premiereDispo` (charge nulle) = t0 : le premier créneau est libre dès t0.
export function chargeActuelle(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
): ChargeActuelle {
  const pizzas = enCharge(commandes).reduce((s, c) => s + c.pizzas, 0);
  const creneaux = Math.ceil(pizzas / config.capaciteParCreneau);
  return {
    pizzas,
    creneaux,
    premiereDispo: premierCreneau(now, config) + creneaux * creneauMs(config),
  };
}

// Première disponibilité estimée pour `nbPizzas` NOUVELLES pizzas, la charge
// engagée passant d'abord (placement conservateur en fin de file).
// Exemple contractuel (§3.7, cap 2 / 5 min) : now=00:20 pile, file vide,
// 5 pizzas -> t0=00:20 -> 3 créneaux -> fin 00:35.
export function premiereDispo(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
  nbPizzas: number,
): number {
  const charge = enCharge(commandes).reduce((s, c) => s + c.pizzas, 0);
  const creneaux = Math.ceil((charge + nbPizzas) / config.capaciteParCreneau);
  return premierCreneau(now, config) + creneaux * creneauMs(config);
}

// Heure PROPOSÉE par Tablier (correctif binding revue 7b — séparation
// sémantique) : première disponibilité estimée + UN créneau de sécurité, dérivé
// de la config (jamais un +5 en dur). La commande validée à l'heure proposée
// naît donc « À l'heure » (`evalPromesse` -> 'ok') ; revenir à la première
// dispo est honnête mais sans marge ('serre' -> « Risque de retard »).
export function heureProposee(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
  nbPizzas: number,
): number {
  return premiereDispo(now, commandes, config, nbPizzas) + creneauMs(config);
}

// Sémantique GRAVÉE (§3.5) : `intenable` = heure strictement antérieure à la
// première dispo · `serre` = heure égale à la première dispo (aucune marge) ·
// `ok` = heure ≥ première dispo + 1 créneau. Une heure dans l'intervalle ouvert
// ]première dispo, première dispo + 1 créneau[ n'offre pas un créneau plein de
// marge -> 'serre'. Heure absente/invalide -> substitution première dispo ->
// 'serre', aucune exception.
export function evalPromesse(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
  heure: number | null | undefined,
  nbPizzas: number,
): EvalPromesse {
  const dispo = premiereDispo(now, commandes, config, nbPizzas);
  if (!heureValide(heure)) return 'serre';
  if (heure < dispo) return 'intenable';
  if (heure >= dispo + creneauMs(config)) return 'ok';
  return 'serre';
}

// Ordonnancement RÉEL de la charge — SOURCE UNIQUE du comparateur (7d) :
// échéance la plus proche d'abord (heure promise croissante ; heure
// absente/invalide -> première dispo courante, §3.5), puis createdAt.
// Partagé par evalDelais ET par « Commencer la suivante » — aucune
// ré-implémentation du tri ailleurs.
export function ordonnerParEcheance(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
): { cmd: ChargeCommande; echeance: number }[] {
  const substitut = chargeActuelle(now, commandes, config).premiereDispo;
  const charge = enCharge(commandes).map((cmd) => ({
    cmd,
    echeance: heureValide(cmd.promise) ? cmd.promise : substitut,
  }));
  charge.sort(
    (a, b) => a.echeance - b.echeance || a.cmd.createdAt - b.cmd.createdAt,
  );
  return charge;
}

// Première commande À PRÉPARER (statut 'commandee') selon le tri moteur —
// cible du bouton « Commencer la suivante » (micro-lot 7d). null si aucune.
export function prochaineAPreparer(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
): number | null {
  return (
    ordonnerParEcheance(now, commandes, config).find(
      ({ cmd }) => cmd.statut === 'commandee',
    )?.cmd.id ?? null
  );
}

// Projection PURE par commande (extension d'API autorisée — arbitrage 11/07) :
// place la charge engagée par échéance la plus proche d'abord (tri
// ordonnerParEcheance ci-dessus) dans les créneaux depuis t0, dérive `fin`
// estimée de CHAQUE commande (fin du créneau de sa dernière pizza), puis
// applique la cascade 4 états. Les statuts hors charge (prete/terminales)
// n'ont AUCUNE entrée.
export function evalDelais(
  now: number,
  commandes: readonly ChargeCommande[],
  config: HeureCapaciteConfig,
): Map<number, StatutDelai> {
  const c = creneauMs(config);
  const t0 = premierCreneau(now, config);
  const charge = ordonnerParEcheance(now, commandes, config);

  const delais = new Map<number, StatutDelai>();
  let placees = 0; // pizzas déjà placées depuis t0
  for (const { cmd, echeance } of charge) {
    placees += cmd.pizzas;
    // Fin estimée = fin du créneau contenant la DERNIÈRE pizza de la commande.
    const fin = t0 + Math.ceil(placees / config.capaciteParCreneau) * c;
    if (now > echeance) {
      delais.set(cmd.id, {
        type: 'retard',
        retardMin: Math.max(1, Math.ceil((now - echeance) / MS_MIN)),
      });
    } else if (fin > echeance) {
      delais.set(cmd.id, { type: 'capacite' });
    } else if (fin > echeance - c) {
      delais.set(cmd.id, { type: 'risque' });
    } else {
      delais.set(cmd.id, { type: 'a_l_heure' });
    }
  }
  return delais;
}

// Adaptateur pur Commande/lignes Dexie -> vue moteur (types seuls, INV-C : les
// données arrivent déjà chargées par les hooks).
export function versChargeCommandes(
  commandes: readonly {
    id?: number;
    statut: StatutCommande;
    heure_retrait_proposee: number;
    createdAt: number;
  }[],
  lignesParCommande: ReadonlyMap<number, readonly { qty: number }[]>,
): ChargeCommande[] {
  return commandes.map((c) => ({
    id: c.id as number,
    pizzas: (lignesParCommande.get(c.id as number) ?? []).reduce(
      (s, l) => s + l.qty,
      0,
    ),
    promise: c.heure_retrait_proposee,
    createdAt: c.createdAt,
    statut: c.statut,
  }));
}
